<template>
  <div>
    <form @submit.prevent="save(!v$.$invalid)">
      <div class="card bg-white">
        <div class="flex justify-content-end mb-2">
          <Button
            :loading="isLoadingSaveHeader"
            :disabled="disableSaveButton"
            icon="pi pi-save"
            class="mr-2"
            label="Simpan"
            type="submit"
          />
          <Button
            icon="pi pi-plus"
            class="p-button-outlined mr-2"
            label="Rincian"
            @click="add"
          />
          <Button
            class="p-button-link"
            label="Kembali"
            @click="$router.push({ name: 'JurnalUmum' })"
          />
        </div>
        <div class="grid">
          <div class="col-12 md:col-6">
            <div class="formgrid grid">
              <div class="field col-12 md:col-4">
                <label>Tanggal</label>
                <Calendar
                  v-model="form.ju_at"
                  dateFormat="dd/mm/yy"
                  class="w-full"
                />
              </div>
              <div class="field col-12 md:col-4">
                <label>No Bukti</label>
                <InputText v-model="form.ju_no" class="w-full" autofocus />
              </div>
              <div class="field col-12">
                <label
                  :class="{
                    'p-error': v$.form.keterangan.$invalid && submitted,
                  }"
                  >Keterangan</label
                >
                <InputText
                  v-model="v$.form.keterangan.$model"
                  :class="{
                    'p-invalid': v$.form.keterangan.$invalid && submitted,
                  }"
                  class="w-full"
                />
                <small
                  v-if="
                    (v$.form.keterangan.$invalid && submitted) ||
                    v$.form.keterangan.$pending.$response
                  "
                  class="p-error"
                  >{{ v$.form.keterangan.required.$message }}</small
                >
              </div>
            </div>
          </div>
        </div>
        <grid-jurnal-umum-detail
          :items="form.detail"
          :loading="isLoading"
          :show-button="true"
          @edit="onEditDetail"
          @delete="onConfirmDeletion"
        />
      </div>
    </form>
    <Dialog
      :header="detailIndex >= 0 ? 'Edit Rincian' : 'Tambah Rincian'"
      v-model:visible="dialogAdd"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <form-perkiraan-jurnal-umum
        :item="detail"
        @close="onCloseFormDetail"
        @save="onSaveDetail"
      />
    </Dialog>
    <Dialog
      header="Hapus Rincian"
      v-model:visible="dialogDelete"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <div>
        <span
          >Rincian <strong>{{ detail.account.nama }}</strong> akan dihapus dari
          daftar rincian. Proses ?</span
        >
      </div>
      <template #footer>
        <Button
          label="Tutup"
          icon="pi pi-times"
          @click="dialogDelete = false"
          class="p-button-text mr-2"
        />
        <Button
          label="Hapus"
          icon="pi pi-trash"
          class="p-button-text p-button-danger"
          @click="onDeleteDetail"
        />
      </template>
    </Dialog>
    <hotkey :shortcuts="['D', 'S']" @triggered="onTriggerHotkey" />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { formatCurrency } from '@/helpers'
import AccountService from '@/services/AccountService'
import errorHandler from '@/helpers/error-handler'
import FormPerkiraanJurnalUmum from '@/components/keuangan/FormPerkiraanJurnalUmum'
import GridJurnalUmumDetail from '@/components/keuangan/GridJurnalUmumDetail'
import Hotkey from '@/components/Hotkey'

export default {
  setup: () => ({ v$: useVuelidate() }),
  props: {
    id: {
      type: Number,
      default: 0,
    },
  },
  components: {
    FormPerkiraanJurnalUmum,
    GridJurnalUmumDetail,
    Hotkey,
  },
  data() {
    return {
      submitted: false,
      accountService: null,
      dialogAdd: false,
      dialogDelete: false,
      isLoading: false,
      isLoadingSaveHeader: false,
      isLoadingSaveDetail: false,
      detailIndex: -1,
      detail: null,
      deleted: [],
      form: {
        id: 0,
        ju_at: new Date(),
        ju_no: '',
        keterangan: null,
        header: '',
        detail: [],
      },
      severity: 'warn',
      disableSaveButton: false,
    }
  },
  created() {
    this.accountService = new AccountService()
  },
  computed: {
    totalDebit() {
      return this.form.detail.reduce((acc, item) => acc + item.debit, 0)
    },
    totalKredit() {
      return this.form.detail.reduce((acc, item) => acc + item.kredit, 0)
    },
    totalBalance() {
      return this.totalDebit - this.totalKredit
    },
  },
  mounted() {
    if (this.id > 0) {
      this.isLoading = true
      this.accountService
        .getJurnal(`/${this.id}`)
        .then((res) => {
          if (res.data.status === 200) {
            this.form = res.data.data
            this.form.detail = res.data.data.detail
            this.form.ju_at = this.form.ju_at ? new Date(this.form.ju_at) : null
          }
        })
        .catch((err) => {
          errorHandler(err, 'Jurnal Umum', this)
        })
        .finally(() => (this.isLoading = false))
    }
  },
  methods: {
    add() {
      this.detailIndex = -1
      this.detail = {
        account: null,
        keterangan: '',
        debit: 0,
        kredit: 0,
      }
      this.dialogAdd = true
    },
    async onEditDetail(item) {
      this.detailIndex = this.form.detail.findIndex(
        (el) => el.id === item.id && el.account.id === item.account.id
      )
      this.detail = Object.assign({}, item)
      this.dialogAdd = true
    },
    onConfirmDeletion(item) {
      this.detail = Object.assign({}, item)
      this.dialogDelete = true
    },
    onDeleteDetail() {
      if (this.detail.id !== 0) {
        this.deleted.push(this.detail.id)
      }
      const index = this.form.detail.findIndex(
        (el) =>
          el.id === this.detail.id && el.account.id === this.detail.account.id
      )
      this.form.detail.splice(index, 1)
      this.detailIndex = -1
      this.detail = {}
      this.dialogDelete = false
    },
    save(isFormValid) {
      console.log(isFormValid)
      this.submitted = true

      if (!isFormValid) {
        return
      }

      if (!this.form.detail) {
        return
      }

      if (this.totalBalance > 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Jurnal Umum',
          detail: `Jurnal belum balance. Selisih sebesar ${formatCurrency(
            this.totalBalance,
            2,
            2
          )}`,
          life: 3000,
        })
        return
      }

      const form = Object.assign({}, this.form)

      form.ju_at = this.form.ju_at
        ? dayjs(this.form.ju_at).format('YYYY-MM-DD HH:mm:ss')
        : null

      if (this.form.detail) {
        let detail = this.form.detail
        detail.forEach((d) => {
          d.account_id = d.account.id
          if (this.form.id > 0) {
            if (d.id == null) {
              d.id = 0
            }
          }
          delete d.account
        })
        form.detail = detail
      }

      if (this.form.id === 0) {
        delete form.id
        this.isLoadingSaveHeader = true
        this.accountService
          .addJurnal(form)
          .then((res) => {
            if (res.data.status === 200) {
              this.$toast.add({
                severity: 'success',
                summary: 'Jurnal Umum',
                detail: 'Data berhasil disimpan.',
                life: 3000,
              })
              this.$router.push({ name: 'JurnalUmum' })
            }
          })
          .catch((err) => {
            errorHandler(err, 'Jurnal Umum', this)
          })
          .finally(() => (this.isLoadingSaveHeader = false))
      } else {
        this.isLoadingSaveHeader = true
        form.deleted = this.deleted
        this.accountService
          .updateJurnal(form)
          .then((res) => {
            if (res.data.status === 200) {
              this.$toast.add({
                severity: 'success',
                summary: 'Jurnal Umum',
                detail: 'Data berhasil disimpan.',
                life: 3000,
              })
            }
          })
          .catch((err) => {
            errorHandler(err, 'Jurnal Umum', this)
          })
          .finally(() => {
            this.deleted = []
            this.isLoadingSaveHeader = false
            this.refreshDetail()
          })
      }
    },
    refreshDetail() {
      if (this.form.id > 0) {
        this.accountService.getJurnal(`/${this.form.id}`).then((res) => {
          if (res.data.status === 200) {
            this.form.detail = res.data.data.detail
          }
        })
      }
    },
    onSaveDetail(item) {
      this.detail = Object.assign({}, item)
      if (this.detailIndex >= 0) {
        Object.assign(this.form.detail[this.detailIndex], this.detail)
        this.dialogAdd = false
      } else {
        /*
        let has_duplicate = false

        this.form.detail.forEach((el) => {
          if (el.account.id === this.detail.account.id) {
            has_duplicate = true
          }
        })

        if (has_duplicate) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Rincian Jurnal Umum',
            detail: 'Duplikasi data rincian! Data tidak dapat ditambahkan.',
            life: 3000,
          })
        } else {
          this.form.detail.push(this.detail)
          this.$toast.add({
            severity: 'success',
            summary: 'Rincian Jurnal Umum',
            detail: 'Data rincian berhasil ditambahkan.',
            life: 3000,
          })
        }
        */
        this.form.detail.push(this.detail)
        this.$toast.add({
          severity: 'success',
          summary: 'Rincian Jurnal Umum',
          detail: 'Data rincian berhasil ditambahkan.',
          life: 3000,
        })
      }
      this.isLoadingSaveDetail = false
    },
    onCloseFormDetail() {
      this.dialogAdd = false
    },
    onTriggerHotkey(payload) {
      switch (payload.keyString) {
        case 'D':
          this.add()
          break
        case 'S':
          this.save(!this.v$.$invalid)
          break
      }
    },
    formatCurrency(value, minDigit = 0, maxDigit = 0) {
      return formatCurrency(value, minDigit, maxDigit)
    },
  },
  validations() {
    return {
      form: {
        keterangan: {
          required: helpers.withMessage('Keterangan harus diisi.', required),
        },
      },
    }
  },
}
</script>
